// =============================================================================
// O ciclo da ferramenta e a conversão do dólar (migration 067)
//
// A conta é a mesma da `v_ferramenta`, repetida aqui para o cadastro mostrar o
// resultado enquanto se digita, antes de salvar. As duas precisam bater ao
// centavo, e por isso a conta é em inteiro (BigInt), nunca em float: o valor
// do ciclo × PTAX × (1 + acréscimo), arredondado como o `round` do Postgres.
// =============================================================================

export type Ciclo = "mensal" | "trimestral" | "semestral" | "anual" | "pontual" | "por_uso";

export const CICLOS: { valor: Ciclo; rotulo: string; meses: number | null; campo: string }[] = [
  { valor: "mensal", rotulo: "Mensal", meses: 1, campo: "Valor mensal" },
  { valor: "trimestral", rotulo: "Trimestral", meses: 3, campo: "Valor trimestral" },
  { valor: "semestral", rotulo: "Semestral", meses: 6, campo: "Valor semestral" },
  { valor: "anual", rotulo: "Anual", meses: 12, campo: "Valor anual" },
  { valor: "pontual", rotulo: "Pontual (compra única)", meses: null, campo: "Valor da compra" },
  { valor: "por_uso", rotulo: "Por uso", meses: 1, campo: "Estimativa por mês" },
];

/**
 * O texto que vai para a coluna "Periodicidade". É o da planilha, e a
 * `fn_ciclo_da_planilha` o lê de volta como o mesmo ciclo.
 */
export const TEXTO_CICLO: Record<Ciclo, string> = {
  mensal: "Mensal", trimestral: "Trimestral", semestral: "Semestral", anual: "Anual", pontual: "Pontual", por_uso: "Por Uso",
};

export type Cotacao = { data: string | null; venda: string | null; acrescimo: string } | null;

/** "5.027700" → { n: 5027700n, casas: 6 }. Só decimal com ponto, sem sinal. */
function decimal(s: string): { n: bigint; casas: number } | null {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s.trim());
  if (!m) return null;
  const casas = m[2]?.length ?? 0;
  return { n: BigInt(m[1] + (m[2] ?? "")), casas };
}

/** Divisão arredondada de positivos, metade para cima — o `round` do Postgres. */
function dividir(a: bigint, b: bigint): bigint {
  return (a * 2n + b) / (b * 2n);
}

/** O valor de um ciclo em centavos de real. Em dólar, pela cotação; sem ela, nulo. */
export function valorEmReais(moeda: "BRL" | "USD", valorCiclo: string | null, cotacao: Cotacao): bigint | null {
  if (valorCiclo === null || valorCiclo === "") return null;
  const v = BigInt(valorCiclo);
  if (moeda === "BRL") return v;
  const venda = cotacao?.venda ? decimal(cotacao.venda) : null;
  const acr = cotacao ? decimal(cotacao.acrescimo) : null;
  if (!venda || !acr) return null;
  // v × (venda / 10^cv) × (1 + acr / 10^ca)  =  v × venda × (10^ca + acr) / 10^(cv + ca)
  const escalaAcr = 10n ** BigInt(acr.casas);
  return dividir(v * venda.n * (escalaAcr + acr.n), 10n ** BigInt(venda.casas) * escalaAcr);
}

export type Calculo = { reaisCiclo: bigint | null; mensal: bigint | null; anual: bigint | null };

/** O mensal equivalente e o custo no ano, como a `v_ferramenta` (067). */
export function calcularCiclo(ciclo: Ciclo | null, moeda: "BRL" | "USD", valorCiclo: string | null, cotacao: Cotacao): Calculo {
  if (!ciclo) return { reaisCiclo: null, mensal: null, anual: null };
  const reais = valorEmReais(moeda, valorCiclo, cotacao);
  if (reais === null) return { reaisCiclo: null, mensal: null, anual: null };
  const meses = CICLOS.find((c) => c.valor === ciclo)!.meses;
  if (meses === null) return { reaisCiclo: reais, mensal: null, anual: reais };
  return {
    reaisCiclo: reais,
    mensal: dividir(reais, BigInt(meses)),
    anual: ciclo === "anual" ? reais : reais * BigInt(12 / meses),
  };
}

/**
 * Ao escolher o ciclo de uma ferramenta que ainda não tinha, o valor sai da
 * coluna da planilha que corresponde a ele — a pessoa confere em vez de
 * redigitar. Em dólar, a planilha guarda o valor do ciclo no "Valor USD".
 */
export function valorDaPlanilha(ciclo: Ciclo, moeda: "BRL" | "USD", f: {
  valor_mensal_centavos: string | null; valor_trimestral_centavos: string | null;
  valor_semestral_centavos: string | null; valor_anual_centavos: string | null; valor_usd_centavos: string | null;
}): string | null {
  if (moeda === "USD") return f.valor_usd_centavos;
  switch (ciclo) {
    case "trimestral": return f.valor_trimestral_centavos;
    case "semestral": return f.valor_semestral_centavos;
    case "anual": return f.valor_anual_centavos;
    default: return f.valor_mensal_centavos;
  }
}

/** "PTAX R$ 5,0277 de 21/10/2026 + 3,5%" — a conta que a tela mostra ao lado do real. */
export function textoCotacao(c: Cotacao): string | null {
  if (!c?.venda || !c.data) return null;
  const [a, m, d] = c.data.split("-");
  const venda = Number(c.venda).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return `PTAX R$ ${venda} de ${d}/${m}/${a} + ${textoAcrescimo(c.acrescimo)}`;
}

/** "0.035" → "3,5%". */
export function textoAcrescimo(fracao: string): string {
  return `${(Number(fracao) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
}
